import config from "@Config/index";
import axios from "axios";

export interface FetchBlogInventoriesParams {
  /** Amber region canonical for the city (e.g. "bath-1811025358760"). */
  cityCanonical?: string;
  /** Amber region canonical for the country (e.g. "united-kingdom-1811011902157"). */
  countryCanonical?: string;
  userCurrency?: string;
  /** Pre-fetched global inventory items (e.g. from SSR). Avoids a global network call. */
  ssrGlobalInventories?: any[];
}

const INVENTORY_LIMIT = 5;

const BASE_FIELDS = {
  only: "id,name,parent_id,pricing,images,videos,location,canonical_name,tags,features,source_link,available,slashed_available_price,provider_id",
  methods:
    "destination_distances,active_children_count,location_coordinates,reviews_count,reviews_rating,meta_short,feature_tags,amber_sales,images_with_featured",
  use_java_api: true,
};

/** Fetch inventories scoped to a city by its amber region canonical name. */
const fetchCityInventories = async (
  cityCanonical: string,
  currency: Record<string, any>,
): Promise<any[]> => {
  const { data } = await axios(`${config.API_URL}/api/v1/inventories`, {
    params: {
      ...BASE_FIELDS,
      p: 1,
      limit: INVENTORY_LIMIT,
      region_canonical_name: cityCanonical,
      region_radius: 20000,
      sort_key: "relevance_sorting_v2",
      sort_order: "desc",
      statuses: "active",
      use_open_search: true,
      isInternalUser: false,
      with_active_rooms: true,
      use_v2_structure: true,
      lang: "en",
      ...currency,
    },
    headers: { "X-Use-Java-Api": "true" },
  });
  return data?.data?.result ?? [];
};

/** Resolve an amber region canonical to its full display name (e.g. "United Kingdom"). */
const resolveCountryLongName = async (countryCanonical: string): Promise<string | undefined> => {
  try {
    const { data } = await axios.get(`${config.API_URL}/api/v0/regions/${countryCanonical}`, {
      params: { lang: "en", use_java_api: true },
    });
    return data?.data?.name || undefined;
  } catch {
    return undefined;
  }
};

/** Fetch inventories scoped to a country long name, or globally if undefined. */
const fetchCountryOrGlobalInventories = async (
  countryLongName: string | undefined,
  currency: Record<string, any>,
): Promise<any[]> => {
  const { data } = await axios(`${config.API_URL}/api/v1/inventories`, {
    params: {
      ...BASE_FIELDS,
      limit: INVENTORY_LIMIT,
      sort_key: "recent_bookings_count",
      sort_order: "desc",
      available: true,
      ...(countryLongName ? { location_country_long_name: countryLongName } : {}),
      ...currency,
    },
    headers: { "X-Use-Java-Api": "true" },
  });
  return data?.data?.result ?? [];
};

/**
 * Fetch global inventories with no location filter.
 * Called at SSR in getBlogHomeData to pre-warm the carousel.
 */
export const fetchGlobalInventories = (currency: Record<string, any> = {}): Promise<any[]> =>
  fetchCountryOrGlobalInventories(undefined, currency);

/**
 * Fetch up to INVENTORY_LIMIT inventories with a fill-down strategy:
 *
 *   1. City results  (if cityCanonical provided)
 *   2. Country results (if countryCanonical provided) — canonical resolved to long name
 *   3. Global results  — skipped if ssrGlobalInventories provided
 *
 * Fill order: city → country → global, deduped by id.
 */
export const fetchBlogInventories = async ({
  cityCanonical,
  countryCanonical,
  userCurrency,
  ssrGlobalInventories,
}: FetchBlogInventoriesParams = {}): Promise<any[]> => {
  const currency = userCurrency ? { user_currency: userCurrency } : {};

  // Fire city fetch and country long-name resolve in parallel.
  const cityPromise = cityCanonical
    ? fetchCityInventories(cityCanonical, currency)
    : Promise.resolve([] as any[]);

  const countryLongNamePromise = countryCanonical
    ? resolveCountryLongName(countryCanonical)
    : Promise.resolve(undefined as string | undefined);

  const [cityResults, countryLongName] = await Promise.all([cityPromise, countryLongNamePromise]);

  // City already covers the full quota — done.
  if (cityResults.length >= INVENTORY_LIMIT) {
    return cityResults.slice(0, INVENTORY_LIMIT);
  }

  const seenIds = new Set<string | number>(cityResults.map((item: any) => item.id));

  // Fire country + global in parallel.
  const countryPromise = countryLongName
    ? fetchCountryOrGlobalInventories(countryLongName, currency)
    : Promise.resolve([] as any[]);

  const globalPromise =
    ssrGlobalInventories !== undefined
      ? Promise.resolve(ssrGlobalInventories)
      : fetchGlobalInventories(currency);

  const [countryResults, globalResults] = await Promise.all([countryPromise, globalPromise]);

  // Dedupe and fill: city → country → global.
  const filled: any[] = [...cityResults];

  for (const item of countryResults) {
    if (filled.length >= INVENTORY_LIMIT) break;
    if (!seenIds.has(item.id)) {
      seenIds.add(item.id);
      filled.push(item);
    }
  }

  if (filled.length < INVENTORY_LIMIT) {
    for (const item of globalResults) {
      if (filled.length >= INVENTORY_LIMIT) break;
      if (!seenIds.has(item.id)) {
        seenIds.add(item.id);
        filled.push(item);
      }
    }
  }

  return filled;
};
